import { morphToPlain } from '~/lib/morph'

export const camelToFlat = camel => {
	return camel.replace(/_/g, ' ').substring(0, 1).toUpperCase() + camel.substring(1)
}

export const copy = obj => JSON.parse(JSON.stringify(obj))

export const noop = a => a

// Force a download (Content-Disposition: attachment) for a CDN media URL, on BOTH clouds from one build (the FE is provider-agnostic — it must never know which cloud it talks to). We emit two params and let each edge pick the one it understands:
//   - `dl=<filename>`                  → AWS CloudFront's viewer-response function reads it and sets Content-Disposition. (CloudFront whitelists only `v` for the cache key/origin, so response-content-disposition is dropped at the edge — harmless on AWS.)
//   - `response-content-disposition`   → Cloud Storage's native override, honored through the Cloud CDN fill (verified). GCS ignores the unknown `dl` param. Neither param is part of the signature/cookie scope, so this is safe with the signed cookie; both are part of the respective cache key so the attachment URL never poisons the inline (player/share) variant. The URL may already carry a ?v= cache-bust, so append with the right separator.
export const withAttachment = (url, filename) => {
	const disposition = `attachment; filename="${filename}"`
	const sep = url.includes('?') ? '&' : '?'
	return `${url}${sep}dl=${encodeURIComponent(filename)}&response-content-disposition=${encodeURIComponent(disposition)}`
}

export const temp_noop = a => {
	console.log('temp_noop', a)
}

export const getTime = Date.now || (() => new Date().getTime())

export const round = value => Math.round(value * 1000 + (value < 0 ? -0.5 : 0.5)) / 1000

export const preventDefault = e => {
	if (e.preventDefault) {
		e.preventDefault()
		if (e.preventManipulation) {
			e.preventManipulation()
		}
	}
}

export const R2D = 180 / Math.PI

export const D2R = Math.PI / 180

export const tod = n => Math.round(n * R2D)

export const dial = (value, length) => (value < 0 ? value + length : value >= length ? value - length : value)

export const flipShape = (shape, angle = 0) => {
	// Normalize angle to 0-180 range since flip line at angle A is the same as angle A+180
	const normalizedAngle = angle % 180

	// Get current properties
	const currentScaleX = shape.scaleX()
	const currentScaleY = shape.scaleY()

	// Get the shape's bounding box to find its center
	const box = shape.getClientRect()
	const centerX = box.x + box.width / 2
	const centerY = box.y + box.height / 2

	// Store original absolute position
	const absPos = shape.absolutePosition()

	// For 90° (vertical line), flip horizontally using scaleX
	if (normalizedAngle === 90) {
		shape.scaleX(-currentScaleX)
	}
	// For 0° (horizontal line), flip vertically using scaleY
	else if (normalizedAngle === 0) {
		shape.scaleY(-currentScaleY)
	}
	// For diagonal lines
	else {
		shape.scaleX(-currentScaleX)
		shape.scaleY(-currentScaleY)
	}

	// Get new bounding box after transformation
	const newBox = shape.getClientRect()
	const newCenterX = newBox.x + newBox.width / 2
	const newCenterY = newBox.y + newBox.height / 2

	// Adjust position to maintain the original center
	const offsetX = centerX - newCenterX
	const offsetY = centerY - newCenterY

	shape.absolutePosition({
		x: absPos.x + offsetX,
		y: absPos.y + offsetY,
	})

	return shape.attrs
}

export const pathToCanvas = (path, x = 0, y = 0, rotation = 0, scaleX = 1, scaleY = 1) => {
	// offset and rotate path
	let i, len, sx, sy, d, angle, points
	path = path.map(segment => {
		points = []
		for (i = 0, len = segment.length; i < len; i += 2) {
			sx = segment[i]
			sy = segment[i + 1]
			d = Math.sqrt(sx * sx + sy * sy)
			angle = Math.atan2(sy, sx) + rotation * D2R
			points[i] = d * Math.cos(angle) * scaleX + x
			points[i + 1] = d * Math.sin(angle) * scaleY + y
		}
		return points
	})

	return ctx => {
		let l, segment, j, i

		ctx.beginPath()

		segment = path[0]
		l = segment.length
		ctx.moveTo(segment[0], segment[1])
		for (i = 2; i < l; i += 6) {
			ctx.bezierCurveTo(
				segment[i],
				segment[i + 1],
				segment[i + 2],
				segment[i + 3],
				segment[i + 4],
				segment[i + 5]
			)
		}

		for (j = 1; j < path.length; j++) {
			segment = path[j]
			l = segment.length
			ctx.moveTo(segment[0], segment[1])
			for (i = 2; i < l; i += 6) {
				ctx.bezierCurveTo(
					segment[i],
					segment[i + 1],
					segment[i + 2],
					segment[i + 3],
					segment[i + 4],
					segment[i + 5]
				)
			}
			ctx.closePath()
		}
	}
}

export const generateName = (items, prop = 'name', value = '', prefix = 'Item ') => {
	let newValue = value?.trim()
	let count = 1
	if (!newValue?.length) newValue = `${prefix}${count}`
	else {
		const match = value.match(/^(.*?)(\d+)$/)
		if (match) {
			prefix = match[1]
			count = parseInt(match[2])
		} else {
			prefix = value + ' '
		}
	}
	if (!items) return newValue

	if (!Array.isArray(items)) {
		items = Object.values(items)
	}
	while (items.some(item => item[prop] === newValue)) {
		count++
		newValue = `${prefix}${count}`
	}

	return newValue
}

const STOP_WORDS = new Set([
	'a',
	'an',
	'and',
	'are',
	'as',
	'at',
	'be',
	'been',
	'but',
	'by',
	'for',
	'from',
	'in',
	'into',
	'is',
	'it',
	'its',
	'like',
	'of',
	'on',
	'or',
	'so',
	'that',
	'the',
	'this',
	'to',
	'was',
	'were',
	'with',
])

export { morphToPlain }

/**
 * The preview ghost a control should actually receive — none, while the value is morphing.
 *
 * ⚠️ **A MORPH OUTRANKS A PREVIEW GHOST.** Both paint the same control, and a static ghost masks the crossfade with the value the param is already morphing to — the morph runs correctly in the store and is invisible on screen.
 *
 * ⚠️ **Still needed even though `PreviewProvider` suppresses every ghost during a local fade.** A morph also arrives from a peer over Ably with no local transition open, and `next_preset_index` is then -1.
 *
 * A preview answers *"what would this become"*; a morph is that change happening. Once it is running the question is answered, so the ghost is suppressed rather than merely reordered.
 *
 * `Field`'s rule, not any one control's: `number`, `range` and `prompt` all accept a ghost (`FormControl`'s `GHOST_VALUE_COMPONENTS`), so a morphable numeric param would mask the same way.
 *
 * ⚠️ Takes the CALLER'S morphing verdict rather than re-deriving it from the value. `isMorphEnvelope` is "is this an object" — the wire type is `T | MorphEnvelope`, so completeness belongs to the server's validator — and `Field` also renders object props: `fill` is `['string','object']`, so a gradient would suppress its own ghost. Only a param the contract declares `morph: true` can carry an envelope, and the caller is the one holding that schema.
 */
export const effectivePreview = (morphing, previewValue) => (morphing ? undefined : previewValue)

export const derivePresetName = (presets, currentIndex, prompt = '') => {
	const words = morphToPlain(prompt)
		.replace(/[^a-zA-Z0-9\s]/g, '')
		.split(/\s+/)
		.filter(w => w && !STOP_WORDS.has(w.toLowerCase()))
	const base = words.slice(0, 2).join(' ').substring(0, 15)
	return generateName(presets, 'name', base, 'Preset ')
}

export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

export const isWebGLSupported = () => {
	const canvas = document.createElement('canvas')
	const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

	if (!gl) {
		console.warn('WebGL not supported - GPU acceleration unavailable')
		return false
	}

	// Get WebGL info
	const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
	if (debugInfo) {
		const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
		const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
		console.info('WebGL Vendor:', vendor)
		console.info('WebGL Renderer:', renderer)
	}

	return true
}

export const isUsingSoftwareRenderer = () => {
	const canvas = document.createElement('canvas')
	const gl = canvas.getContext('webgl')
	if (!gl) return true // No WebGL at all

	const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
	if (!debugInfo) return true // Can't determine renderer

	const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
	return renderer.includes('SwiftShader') || renderer.includes('llvmpipe')
	// renderer.includes('ANGLE') ||
}

/**
 * Converts a hex color string to an RGBA array of normalized values (0.0 - 1.0)
 * @param {string} hex - Hex color string (e.g., "#FF0000", "#FF0000FF", "FF0000", etc.)
 * @param {number} [defaultAlpha=1.0] - Default alpha value if not specified in hex
 * @returns {number[]} RGBA values as array of floats from 0.0-1.0
 */
export function hexToRgba(hex, defaultAlpha = 1.0) {
	// Remove # if present
	hex = hex.replace(/^#/, '')

	let r,
		g,
		b,
		a = defaultAlpha

	// Handle different hex formats
	if (hex.length === 3 || hex.length === 4) {
		// #RGB or #RGBA -> convert to #RRGGBB or #RRGGBBAA
		r = parseInt(hex[0] + hex[0], 16) / 255
		g = parseInt(hex[1] + hex[1], 16) / 255
		b = parseInt(hex[2] + hex[2], 16) / 255

		if (hex.length === 4) {
			a = parseInt(hex[3] + hex[3], 16) / 255
		}
	} else if (hex.length === 6 || hex.length === 8) {
		// #RRGGBB or #RRGGBBAA
		r = parseInt(hex.substring(0, 2), 16) / 255
		g = parseInt(hex.substring(2, 4), 16) / 255
		b = parseInt(hex.substring(4, 6), 16) / 255

		if (hex.length === 8) {
			a = parseInt(hex.substring(6, 8), 16) / 255
		}
	} else {
		// Invalid format, return fallback color (red)
		console.error('Invalid hex color format:', hex)
		return [1.0, 0.0, 0.0, 1.0]
	}

	return [r, g, b, a]
}

export const getShortFileName = (url, maxLength = 20) => {
	const name = url.split('/').pop()
	return name.length > maxLength ? name.substring(0, maxLength - 3) + '...' : name
}

export const isNo = str => str === '0' || str === 'false' || str === 'no'

export const getDevice = () => {
	let device = 'Unknown'
	const ua = {
		Linux: /Linux/i,
		Android: /Android/i,
		BlackBerry: /BlackBerry/i,
		Bluebird: /EF500/i,
		'Chrome OS': /CrOS/i,
		Datalogic: /DL-AXIS/i,
		Honeywell: /CT50/i,
		iPad: /iPad/i,
		iPhone: /iPhone/i,
		iPod: /iPod/i,
		macOS: /Macintosh/i,
		Windows: /IEMobile|Windows/i,
		Zebra: /TC70|TC55/i,
	}
	Object.keys(ua).map(v => navigator.userAgent.match(ua[v]) && (device = v))
	return device
}

export const formatCredits = credits => {
	let v = credits
	if (!v) v = '0'
	else {
		v = v.toString()
		const i = v.indexOf('.')
		if (i > 0) v = v.slice(0, i)
	}
	return `${v} ₵`
}
